/**
 * Render-time report preparation — filters and transforms applied on
 * read without mutating persisted Firestore/GCS records.
 */
export {
  UNAVAILABLE_PATTERNS,
  filterUnavailableSections,
  getUnavailableSectionIds,
  isUnavailableBody,
  refMatchesSection,
  type ReportRenderSection,
} from './filterUnavailableSections';
export {
  INTERNAL_SENTINEL_PATTERNS,
  INSUFFICIENT_EVIDENCE_CAVEAT,
  WITHHELD_NARRATIVE_NOTICE,
  containsInternalSentinel,
  containsInternalSentinelDeep,
  isWithheldNarrativeNotice,
  materializeWithheldNarrative,
  scrubSentinelBody,
  scrubInternalSentinels,
  scrubInternalSentinelsDeep,
} from './scrubInternalSentinels';
export {
  computeEffectiveAppendixVisibleRefIndices,
  buildRefNumberSourceMap,
} from './resolveRefMap';
export {
  normalizeDashes,
  normalizeReportDashes,
} from './normalizeReportDashes';

import type { SiteRagReport } from '../../types/siteRag';
import { filterUnavailableSections } from './filterUnavailableSections';
import { materializeWithheldNarrative, scrubInternalSentinelsDeep } from './scrubInternalSentinels';
import { stripConfidenceComments, stripConfidenceCommentsDeep } from './narrativeArtifacts';
export { stripConfidenceComments, stripConfidenceCommentsDeep };
import { normalizeReportDashes } from './normalizeReportDashes';
import { withDerivedTableReferences } from '../report/derivedTableReferences';

function reconcileVerdictPhrasing<T extends Pick<SiteRagReport, 'qualityWarnings'>>(report: T): T {
  const candidate = report as unknown as Partial<SiteRagReport>;
  const rec = candidate.conclusion?.recommendation ?? '';
  const isNoGoRecommendation = /^(?:\s*Do\s+Not\s+Proceed\b|\s*No[- ]Go\b)/i.test(rec);
  const fixVerdictPhrase = (text: string | undefined) => {
    if (typeof text !== 'string') return text;
    if (!isNoGoRecommendation) return text;
    let nextText = text;
    if (/\bsupporting a conditional go recommendation\b/i.test(nextText)) {
      nextText = nextText.replace(
        /\bsupporting a conditional go recommendation\b/gi,
        'supporting a challenging screening position (do not proceed until binding conditions are resolved)',
      );
    }
    if (/\brecommends\s+a\s+(?:conditional\s+)?go\b/i.test(nextText)) {
      nextText = nextText.replace(
        /\brecommends\s+a\s+(?:conditional\s+)?go\b/gi,
        'identifies a challenging screening position (do not proceed until binding conditions are resolved)',
      );
    }
    return nextText;
  };
  let nextExec = fixVerdictPhrase(candidate.executiveSummary);
  let nextSum = fixVerdictPhrase(candidate.summary);
  let nextOverview = candidate.projectIntentOverview;
  // GOLDENROD 1: When executiveSummary has been updated with canonical basis/phase
  // qualifications (e.g. "facility demand" / "intact" / "N-1") while summary or
  // projectIntentOverview still holds the pre-qualification duplicate paragraph,
  // synchronize summary and deduplicate/update projectIntentOverview.
  if (
    typeof nextExec === 'string' &&
    nextExec.trim().length > 0 &&
    typeof nextSum === 'string' &&
    nextSum.trim().length > 0 &&
    nextExec.trim() !== nextSum.trim() &&
    nextExec.slice(0, 48) === nextSum.slice(0, 48)
  ) {
    if (typeof nextOverview === 'string' && nextOverview.includes(nextSum.trim())) {
      nextOverview = nextOverview.replace(nextSum.trim(), nextExec.trim());
    }
    nextSum = nextExec;
  }

  // GOLDENROD 2: Reconcile self-contradictory capacityCreditDeepDive.summary
  // clauses where a phase states its N-1 residual firm capacity alongside a
  // positive N-0 intact margin when perPhase[i].firmDeliverableMW holds the
  // higher intact N-0 sum (derived dynamically from deepDive.perPhase).
  let nextPowerAnalysis = candidate.powerAnalysis;
  const deepDive = nextPowerAnalysis?.capacityCreditDeepDive;
  if (
    deepDive &&
    typeof deepDive.summary === 'string' &&
    Array.isArray(deepDive.perPhase) &&
    deepDive.perPhase.length > 0
  ) {
    let correctedDeepDiveSummary = deepDive.summary;
    for (const p of deepDive.perPhase) {
      const phaseLabel = String(p.phaseLabel ?? '').trim();
      const intactFirm = String(p.firmDeliverableMW ?? '').trim();
      const facilityLoad = String(p.facilityLoadMW ?? '').trim();
      const requiredFirm = String(p.requiredFirmMW ?? '').trim();
      const perSource = String(p.perSourceElcc ?? '').trim();
      const intactVal = parseFloat(intactFirm.replace(/,/g, ''));
      const reqVal = parseFloat(requiredFirm.replace(/,/g, ''));
      if (!phaseLabel || !Number.isFinite(intactVal) || !Number.isFinite(reqVal)) continue;
      const escPhase = phaseLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const formARe = new RegExp(
        `For\\s+${escPhase},\\s*estimated\\s+planning-default\\s+ELCC\\s+firm-deliverable\\s+capacity\\s+is\\s+([\\d.,]+)\\s*MW\\s+against\\s+a\\s+([\\d.,]+\\s*MW)\\s+facility\\s+load\\s+and\\s+([\\d.,]+\\s*MW)\\s+required\\s+firm\\s+capacity\\s*\\((?:marginally\\s+)?feasible\\s+with\\s+a\\s+tight\\s+\\+([\\d.,]+\\s*MW)\\s+margin([^)]*)\\)\\.`,
        'i',
      );
      correctedDeepDiveSummary = correctedDeepDiveSummary.replace(
        formARe,
        (full, statedMw: string, loadMw: string, reqMw: string, marginMw: string, tail: string) => {
          const statedVal = parseFloat(statedMw.replace(/,/g, ''));
          if (!Number.isFinite(statedVal) || Math.abs(statedVal - intactVal) < 0.05) return full;
          const breakdown = perSource ? ` (${perSource})` : '';
          return `For ${phaseLabel}, estimated intact planning-default ELCC firm-deliverable capacity is ${intactFirm}${breakdown} against a ${loadMw || facilityLoad} facility load and ${reqMw || requiredFirm} required firm capacity (marginally feasible under intact N-0 planning-default ELCC assumptions with a tight +${marginMw} intact margin — not utility-verified headroom — while single-contingency N-1 loss leaves only ${statedMw} MW of remaining firm capacity${tail}).`;
        },
      );
      const formBRe = new RegExp(
        `In\\s+${escPhase},\\s*cumulative\\s+firm-deliverable\\s+capacity\\s+is\\s+([\\d.,]+)\\s*MW\\s+against\\s+a\\s+([\\d.,]+\\s*MW)\\s+requirement\\s*\\(([^)]*)\\)\\s*[—–-]\\s*(?:marginally\\s+)?feasible\\s*\\(\\+([\\d.,]+\\s*MW)\\s+margin,\\s*([^)]+(?:\\([^)]*\\)[^)]*)?)\\)\\.`,
        'i',
      );
      correctedDeepDiveSummary = correctedDeepDiveSummary.replace(
        formBRe,
        (full, statedMw: string, reqMw: string, reserveNote: string, marginMw: string, n1Note: string) => {
          const statedVal = parseFloat(statedMw.replace(/,/g, ''));
          if (!Number.isFinite(statedVal) || Math.abs(statedVal - intactVal) < 0.05) return full;
          const breakdown = perSource ? ` (${perSource})` : '';
          return `In ${phaseLabel}, cumulative intact planning-default ELCC firm-deliverable capacity is ${intactFirm}${breakdown} against a ${reqMw || requiredFirm} requirement (${reserveNote}) — marginally feasible under intact N-0 planning-default ELCC assumptions (+${marginMw} intact margin; not utility-verified headroom), while single-contingency N-1 loss leaves only ${statedMw} MW of remaining firm capacity (${n1Note}).`;
        },
      );
    }
    if (correctedDeepDiveSummary !== deepDive.summary) {
      nextPowerAnalysis = {
        ...nextPowerAnalysis!,
        capacityCreditDeepDive: {
          ...deepDive,
          summary: correctedDeepDiveSummary,
        },
      };
    }
  }

  // GOLDENROD 3: Qualify undeclared BESS in powerAnalysis, btmOptions, gridStudy,
  // developmentRisks, and risks when no phase in projectIntentAtGen declares BESS/battery storage.
  const declaredPhases = candidate.projectIntentAtGen?.phases ?? [];
  const declaresBess = declaredPhases.some((p) =>
    (p.powerSourceConfig?.components ?? []).some((c) => /bess|battery/i.test(String(c))),
  ) || /bess|battery/i.test(String((candidate.projectIntentAtGen as { powerSource?: unknown } | undefined)?.powerSource ?? ''));
  let nextBtmOptions = candidate.btmOptions;
  let nextGridStudy = candidate.gridStudy;
  let nextRisks = candidate.risks;
  let nextDevRisks = (candidate as { developmentRisks?: Array<Record<string, unknown>> }).developmentRisks;
  if (declaredPhases.length > 0 && !declaresBess) {
    if (Array.isArray(nextRisks)) {
      let risksChanged = false;
      const mappedRisks = nextRisks.map((r) => {
        if (typeof r.mitigation === 'string' && /modular gas\/BESS capacity/i.test(r.mitigation)) {
          risksChanged = true;
          return {
            ...r,
            mitigation: r.mitigation.replace(
              /modular gas\/BESS capacity/gi,
              'modular gas capacity (or optional storage if added to project intent)',
            ),
          };
        }
        return r;
      });
      if (risksChanged) nextRisks = mappedRisks;
    }
    if (Array.isArray(nextDevRisks)) {
      let devRisksChanged = false;
      const mappedDevRisks = nextDevRisks.map((r) => {
        if (typeof r.action === 'string' && /modular gas\/BESS capacity/i.test(r.action)) {
          devRisksChanged = true;
          return {
            ...r,
            action: r.action.replace(
              /modular gas\/BESS capacity/gi,
              'modular gas capacity (or optional storage if added to project intent)',
            ),
          };
        }
        return r;
      });
      if (devRisksChanged) nextDevRisks = mappedDevRisks;
    }
    if (nextPowerAnalysis) {
      let nextRenewable = nextPowerAnalysis.renewableIntegration;
      if (typeof nextRenewable === 'string') {
        nextRenewable = nextRenewable
          .replace(
            /On-site battery energy storage systems \(BESS\) with grid-forming inverters remain highly applicable to smooth synchronized GPU training load fluctuations and provide sub-second ride-through/gi,
            'Optional on-site battery energy storage systems (BESS) with grid-forming inverters (not declared in the current Phase 1–2 project intent; requires a separate architecture change and transient study) could be evaluated to smooth synchronized GPU training load fluctuations and provide sub-second ride-through',
          )
          .replace(
            /grid-forming BESS and fast-ramping reciprocating engines remain highly applicable to buffer rapid AI load steps and intermittent generation/gi,
            'fast-ramping generation controls or optional energy storage (BESS is not declared in the current Phase 1–2 project intent and would require a separate architecture change and transient study) can be evaluated if buffering of rapid AI load steps or intermittent generation is required',
          );
      }
      let nextRedundancy = nextPowerAnalysis.redundancyAssessment;
      if (typeof nextRedundancy === 'string') {
        nextRedundancy = nextRedundancy.replace(
          /additional on-site generation and BESS reserves/gi,
          'additional on-site generation (or optional BESS reserves if added to the declared phase architecture)',
        );
      }
      if (
        nextRenewable !== nextPowerAnalysis.renewableIntegration ||
        nextRedundancy !== nextPowerAnalysis.redundancyAssessment
      ) {
        nextPowerAnalysis = {
          ...nextPowerAnalysis,
          ...(nextRenewable !== undefined ? { renewableIntegration: nextRenewable } : {}),
          ...(nextRedundancy !== undefined ? { redundancyAssessment: nextRedundancy } : {}),
        };
      }
    }
    if (Array.isArray(nextBtmOptions)) {
      let btmChanged = false;
      const mappedBtm = nextBtmOptions.map((opt) => {
        if (!/battery|\bbess\b/i.test(String(opt.technology ?? ''))) return opt;
        const rawOpt = opt as unknown as Record<string, unknown>;
        const nextPros = typeof rawOpt.pros === 'string'
          ? rawOpt.pros.replace(
              /is essential for buffering instantaneous GPU load swings/gi,
              'can be evaluated as an optional non-declared alternative if transient studies show instantaneous GPU load swings',
            )
          : rawOpt.pros;
        const nextRelevance = typeof rawOpt.relevance === 'string'
          ? rawOpt.relevance.replace(
              /^Required to bridge the gap between turbine ramp rates and millisecond-level AI load fluctuations/i,
              'Not declared in the current Phase 1–2 power mix; evaluate only if a site-specific power-systems transient study identifies a ride-through gap between turbine ramp rates and AI load fluctuations',
            )
          : rawOpt.relevance;
        const nextAction = typeof rawOpt.action === 'string'
          ? rawOpt.action.replace(
              /Incorporate grid-forming BESS into the on-site electrical architecture for GPU load smoothing and UPS ride-through/gi,
              'If transient studies identify a ride-through gap, evaluate adding optional grid-forming BESS (not declared in the current Phase 1–2 power mix) for GPU load smoothing and UPS ride-through',
            )
          : rawOpt.action;
        const nextReason = typeof rawOpt.reason === 'string'
          ? rawOpt.reason.replace(
              /However,\s*grid-forming BESS is essential to buffer high-frequency synchronized GPU power swings during AI training and to provide short-duration ride-through during source contingencies/gi,
              'However, optional grid-forming BESS (not declared in the current Phase 1–2 project intent) can be evaluated if a site-specific transient study shows it is needed to buffer high-frequency synchronized GPU power swings during AI training and provide short-duration ride-through during source contingencies',
            )
          : rawOpt.reason;
        const nextSuitability = typeof rawOpt.suitability === 'string'
          ? rawOpt.suitability.replace(
              /BESS for transient ride-through/gi,
              'optional BESS not declared in current Phase 1–2 mix; evaluate only if added for transient ride-through',
            )
          : rawOpt.suitability;
        if (
          nextPros !== rawOpt.pros ||
          nextRelevance !== rawOpt.relevance ||
          nextAction !== rawOpt.action ||
          nextReason !== rawOpt.reason ||
          nextSuitability !== rawOpt.suitability
        ) {
          btmChanged = true;
          return {
            ...opt,
            ...(nextPros !== undefined ? { pros: nextPros } : {}),
            ...(nextRelevance !== undefined ? { relevance: nextRelevance } : {}),
            ...(nextAction !== undefined ? { action: nextAction } : {}),
            ...(nextReason !== undefined ? { reason: nextReason } : {}),
            ...(nextSuitability !== undefined ? { suitability: nextSuitability } : {}),
          } as typeof opt;
        }
        return opt;
      });
      if (btmChanged) nextBtmOptions = mappedBtm;
    }
    if (nextGridStudy) {
      let gridChanged = false;
      const rawGrid = nextGridStudy as unknown as Record<string, unknown>;
      let nextIslanding = rawGrid.islandingReadiness as { blackStartCapability?: string; [k: string]: unknown } | undefined;
      if (nextIslanding && typeof nextIslanding.blackStartCapability === 'string') {
        const updatedBlackStart = nextIslanding.blackStartCapability
          .replace(
            /requires grid-forming BESS and black-start diesel\/gas exciters/gi,
            'requires dedicated black-start capability such as black-start diesel/gas exciters or optional grid-forming BESS (neither of which is declared in the current Phase 1–2 mix)',
          )
          .replace(
            /requires dedicated black-start diesel generators or grid-forming BESS/gi,
            'requires dedicated black-start capability (such as black-start diesel units or optional grid-forming storage, neither of which is declared in the current phase mix)',
          );
        if (updatedBlackStart !== nextIslanding.blackStartCapability) {
          gridChanged = true;
          nextIslanding = {
            ...nextIslanding,
            blackStartCapability: updatedBlackStart,
          };
        }
      }
      const rawFirmN1 = rawGrid.firmCapacityN1 as Record<string, unknown> | undefined;
      let nextFirmN1 = rawFirmN1;
      if (rawFirmN1 && typeof rawFirmN1.redundancyToClose === 'string') {
        const updatedRedundancyToClose = rawFirmN1.redundancyToClose.replace(
          /incorporating long-duration BESS bridge capacity/gi,
          'evaluating optional long-duration BESS bridge capacity (not declared in the current Phase 1–2 mix)',
        );
        if (updatedRedundancyToClose !== rawFirmN1.redundancyToClose) {
          gridChanged = true;
          nextFirmN1 = {
            ...rawFirmN1,
            redundancyToClose: updatedRedundancyToClose,
          };
        }
      }
      let nextGridRecs = rawGrid.recommendations;
      if (typeof nextGridRecs === 'string' && /modular BTM gas\/BESS expansion/i.test(nextGridRecs)) {
        gridChanged = true;
        nextGridRecs = nextGridRecs.replace(
          /modular BTM gas\/BESS expansion/gi,
          'modular BTM gas expansion (or optional storage if added to project intent)',
        );
      } else if (Array.isArray(nextGridRecs)) {
        let recsArrChanged = false;
        const mappedRecs = nextGridRecs.map((rec) => {
          if (typeof rec === 'string' && /modular BTM gas\/BESS expansion/i.test(rec)) {
            recsArrChanged = true;
            return rec.replace(
              /modular BTM gas\/BESS expansion/gi,
              'modular BTM gas expansion (or optional storage if added to project intent)',
            );
          }
          return rec;
        });
        if (recsArrChanged) {
          gridChanged = true;
          nextGridRecs = mappedRecs;
        }
      }
      if (gridChanged) {
        nextGridStudy = {
          ...nextGridStudy,
          ...(nextIslanding !== undefined ? { islandingReadiness: nextIslanding } : {}),
          ...(nextFirmN1 !== undefined ? { firmCapacityN1: nextFirmN1 } : {}),
          ...(nextGridRecs !== undefined ? { recommendations: nextGridRecs as string[] } : {}),
        };
      }
    }
  }

  if (
    nextExec !== candidate.executiveSummary ||
    nextSum !== candidate.summary ||
    nextOverview !== candidate.projectIntentOverview ||
    nextPowerAnalysis !== candidate.powerAnalysis ||
    nextBtmOptions !== candidate.btmOptions ||
    nextGridStudy !== candidate.gridStudy ||
    nextRisks !== candidate.risks ||
    nextDevRisks !== (candidate as { developmentRisks?: unknown }).developmentRisks
  ) {
    return {
      ...report,
      ...(nextExec !== undefined ? { executiveSummary: nextExec } : {}),
      ...(nextSum !== undefined ? { summary: nextSum } : {}),
      ...(nextOverview !== undefined ? { projectIntentOverview: nextOverview } : {}),
      ...(nextPowerAnalysis !== undefined ? { powerAnalysis: nextPowerAnalysis } : {}),
      ...(nextBtmOptions !== undefined ? { btmOptions: nextBtmOptions } : {}),
      ...(nextGridStudy !== undefined ? { gridStudy: nextGridStudy } : {}),
      ...(nextRisks !== undefined ? { risks: nextRisks } : {}),
      ...(nextDevRisks !== undefined ? { developmentRisks: nextDevRisks } : {}),
    };
  }
  return report;
}

/**
 * Narrative-text hygiene shared by EVERY customer surface (UI, PDF, and the
 * server DOCX/PPTX/CSV projector): show the honest "not assessed" notice at
 * each leaf the reviewer-voice gate withheld, then deep-scrub every string
 * leaf of any legacy internal failure sentinel. Reference-preserving; a clean
 * report comes back as the same object.
 */
export function prepareReportTextForProjection<T extends Pick<SiteRagReport, 'qualityWarnings'>>(report: T): T {
  return reconcileVerdictPhrasing(stripConfidenceCommentsDeep(scrubInternalSentinelsDeep(materializeWithheldNarrative(report))));
}

/**
 * {@link prepareReportTextForProjection} over a list of reports for read-only
 * surfaces that consume RAW report fields (the Findings dossier lanes read
 * `conclusion.*`, `recommendations[]`, `risks[]`, `crossReportSynthesis[]`
 * and the frozen `decisionAnalysis`). Returns the SAME array when no report
 * changed, so memoised consumers stay stable. Presentation only: never
 * persist or re-verify an element of the result.
 */
const presentedReportCache = new WeakMap<object, SiteRagReport>();

export function presentReportsText<T extends SiteRagReport>(reports: readonly T[]): readonly T[] {
  let changed = false;
  const next = reports.map((report) => {
    if (report && typeof report === 'object') {
      const cached = presentedReportCache.get(report) as T | undefined;
      if (cached) {
        if (cached !== report) changed = true;
        return cached;
      }
    }
    const presented = prepareReportTextForProjection(report);
    if (report && typeof report === 'object') {
      presentedReportCache.set(report, presented);
      if (presented && typeof presented === 'object') {
        presentedReportCache.set(presented, presented);
      }
    }
    if (presented !== report) changed = true;
    return presented;
  });
  return changed ? next : reports;
}

const AUTOMATED_ELEVATION_PROXY_SOURCE_RE =
  /\b(?:google[-_\s]*elevation|usgs[-_\s]*epqs|elevation[-_\s]*proxy|elevation[-_\s]*relief|refdata[-_\s]*3dep|3dep[-_\s]*regional|polygon[-_\s]*sample|deterministic\s+elevation[-_\s]*relief)\b/i;

const RECONCILED_UNVERIFIED_SLOPE_EXPLANATION_RE =
  /Terrain slope unverified \((?:single-point elevation sample|0% elevation-relief proxy)/i;

function parseFiniteSlopeNumber(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const match = /^([-+]?\d+(?:\.\d+)?)\s*%?(?:\s+slope)?$/i.exec(trimmed);
    if (!match) return null;
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getSiteTopographyMetricRecord(report: SiteRagReport): Record<string, unknown> | undefined {
  const rawCats = (report as unknown as { categories?: unknown }).categories;
  if (Array.isArray(rawCats)) {
    const siteCat = rawCats.find(
      (c): c is Record<string, unknown> => Boolean(c && typeof c === 'object' && (c as { id?: unknown }).id === 'site'),
    );
    if (!siteCat) return undefined;
    const rawMetrics = siteCat.metrics;
    if (Array.isArray(rawMetrics)) {
      return rawMetrics.find(
        (m): m is Record<string, unknown> =>
          Boolean(m && typeof m === 'object' && (m as { id?: unknown }).id === 'topography'),
      );
    }
    if (rawMetrics && typeof rawMetrics === 'object') {
      const keyed = (rawMetrics as Record<string, unknown>).topography;
      if (keyed && typeof keyed === 'object') return keyed as Record<string, unknown>;
    }
    return undefined;
  }
  if (rawCats && typeof rawCats === 'object') {
    const siteCat = (rawCats as Record<string, unknown>).site;
    if (siteCat && typeof siteCat === 'object') {
      const rawMetrics = (siteCat as Record<string, unknown>).metrics;
      if (Array.isArray(rawMetrics)) {
        return rawMetrics.find(
          (m): m is Record<string, unknown> =>
            Boolean(m && typeof m === 'object' && (m as { id?: unknown }).id === 'topography'),
        );
      }
      if (rawMetrics && typeof rawMetrics === 'object') {
        const keyed = (rawMetrics as Record<string, unknown>).topography;
        if (keyed && typeof keyed === 'object') return keyed as Record<string, unknown>;
      }
    }
  }
  return undefined;
}

export const NEGATED_OR_MISSING_TOPOGRAPHY_RE =
  /\b(?:not\s+performed|not\s+conducted|not\s+included|not\s+available|unavailable|no\s+(?:slope|topograph\w*|contour\w*|elevation\w*|grading)|without\s+(?:slope|topograph\w*|contour\w*|elevation\w*)|boundary[-\s]*only|excludes?\s+(?:topograph\w*|contour\w*|elevation\w*)|omits?\s+(?:topograph\w*|contour\w*|elevation\w*)|table\s+a\s+item\s+5\s+(?:not\s+selected|omitted|excluded))\b/i;

const UNAVAILABLE_OR_UNVERIFIED_SOURCE_STATE_SET = new Set([
  'unavailable',
  'missing',
  'unknown',
  'unverified',
  'conflict',
  'unverified-conflict',
  'failed',
  'rejected',
  'skipped',
  'empty',
  'anomalous',
  'screening',
  'estimated',
  'draft',
  'held',
  'degraded',
  'pending-review',
]);

const VERIFIED_SOURCE_STATE_SET = new Set(['verified', 'verified-from-doc']);

const ALLOWED_TOPOGRAPHY_SOURCE_GRADES = new Set([
  'verified-professional',
  'authoritative-government',
]);

const TOPOGRAPHY_BOUND_FIELD_SET = new Set([
  'land.slope',
  'slope',
  'topography',
  'topography.slope',
  'site.topography',
  'metric:site:topography',
]);

const ALLOWED_TOPOGRAPHY_DERIVATION_METHODS = new Set([
  'field-topographic-survey',
  'alta-table-a-item-5-contours',
  'lidar-tin-grade',
  'civil-grading-survey',
]);

/**
 * Evaluates whether a single resolved source/reference/document record provides
 * structured, available, verified, field-bound topographic slope measurement support.
 *
 * Schema gap note (#240 #issuecomment-6527214364):
 * Document titles, filenames, or document types (`ALTA`, `geotechnical`, `topographic survey`)
 * never establish numerical slope support. Current `DataSource` (`src/types/siteRag.ts`) and
 * `documentCitationV1Schema` (`src/types/reportContractsV2.ts`) do not carry a first-class
 * structured `topographicSupport` schema field (`measuredSlopePct`, `topographicContours`,
 * `contourIntervalFt`/`contourIntervalM`, `surveyPointsSampled`, `derivationMethod`), and
 * `documentCitationV1Schema` also omits `qualification` and `sourceGrade`. Records lacking
 * explicit structured availability, field binding, and structured measurement support fail closed.
 */
export function isRecordStructuredTopographySurveySupport(
  rec: Record<string, unknown>,
  reportSiteId: string | undefined,
  expectedSlopePct: number | null,
): boolean {
  // Gate 1: Same-site scope, non-synthetic, and non-proxy check
  if (rec.siteId !== undefined && rec.siteId !== reportSiteId) return false;
  if (rec.demonstrationDisclosure != null) return false;
  const attribution =
    rec.attribution && typeof rec.attribution === 'object'
      ? (rec.attribution as Record<string, unknown>)
      : undefined;
  if (
    attribution?.appliesToProject &&
    attribution.appliesToProject !== 'this-site' &&
    attribution.appliesToProject !== reportSiteId
  ) {
    return false;
  }

  // Gate 2: Structured availability and verified qualification (fail closed if unavailable or unverified)
  if (rec.available === false || rec.missing === true) return false;
  const stateFields = [
    rec.qualification,
    rec.status,
    rec.state,
    rec.reviewState,
    rec.coverageStatus,
  ];
  for (const rawState of stateFields) {
    if (typeof rawState === 'string') {
      const normalized = rawState.trim().toLowerCase();
      if (UNAVAILABLE_OR_UNVERIFIED_SOURCE_STATE_SET.has(normalized)) {
        return false;
      }
    }
  }
  const qualNorm = typeof rec.qualification === 'string' ? rec.qualification.trim().toLowerCase() : '';
  const statusNorm = typeof rec.status === 'string' ? rec.status.trim().toLowerCase() : '';
  const hasPositiveVerifiedState =
    VERIFIED_SOURCE_STATE_SET.has(qualNorm) || VERIFIED_SOURCE_STATE_SET.has(statusNorm);
  if (!hasPositiveVerifiedState) return false;

  const gradeRaw = rec.sourceGrade ?? rec.grade;
  if (typeof gradeRaw === 'string') {
    if (!ALLOWED_TOPOGRAPHY_SOURCE_GRADES.has(gradeRaw.trim().toLowerCase())) {
      return false;
    }
  }

  // Gate 3: Explicit metric/field binding to `land.slope` / `topography`
  const candidateFields: string[] = [];
  for (const f of [rec.field, rec.path, rec.sourceField, rec.metricId]) {
    if (typeof f === 'string' && f.trim().length > 0) candidateFields.push(f.trim().toLowerCase());
  }
  for (const arr of [rec.supportedFields, rec.fields, rec.metrics]) {
    if (Array.isArray(arr)) {
      for (const item of arr) {
        if (typeof item === 'string' && item.trim().length > 0) {
          candidateFields.push(item.trim().toLowerCase());
        }
      }
    }
  }
  if (!candidateFields.some((f) => TOPOGRAPHY_BOUND_FIELD_SET.has(f))) {
    return false;
  }

  // Gate 4: Explicit structured topographic measurement/derivation support
  const supportObj =
    rec.topographicSupport && typeof rec.topographicSupport === 'object' && !Array.isArray(rec.topographicSupport)
      ? (rec.topographicSupport as Record<string, unknown>)
      : rec.measurements && typeof rec.measurements === 'object' && !Array.isArray(rec.measurements)
        ? (rec.measurements as Record<string, unknown>)
        : undefined;
  if (!supportObj) return false;
  if (supportObj.siteId !== undefined && supportObj.siteId !== reportSiteId) {
    return false;
  }

  const measuredSlopePct = supportObj.measuredSlopePct;
  if (typeof measuredSlopePct !== 'number' || !Number.isFinite(measuredSlopePct)) {
    return false;
  }
  if (expectedSlopePct != null && Math.abs(measuredSlopePct - expectedSlopePct) >= 1e-6) {
    return false;
  }

  const contourIntervalFt = supportObj.contourIntervalFt;
  const contourIntervalM = supportObj.contourIntervalM;
  const hasValidContours =
    supportObj.topographicContours === true &&
    ((typeof contourIntervalFt === 'number' && Number.isFinite(contourIntervalFt) && contourIntervalFt > 0) ||
      (typeof contourIntervalM === 'number' && Number.isFinite(contourIntervalM) && contourIntervalM > 0));

  const surveyPointsSampled = supportObj.surveyPointsSampled;
  const derivationMethod =
    typeof supportObj.derivationMethod === 'string'
      ? supportObj.derivationMethod.trim().toLowerCase()
      : '';
  const hasValidSurveyDerivation =
    typeof surveyPointsSampled === 'number' &&
    Number.isFinite(surveyPointsSampled) &&
    surveyPointsSampled >= 2 &&
    ALLOWED_TOPOGRAPHY_DERIVATION_METHODS.has(derivationMethod);

  if (!hasValidContours && !hasValidSurveyDerivation) {
    return false;
  }

  // Gate 5: Textual negation / boundary-only / proxy exclusion across all descriptive fields
  const textFragments: string[] = [];
  for (const v of [
    rec.label,
    rec.title,
    rec.name,
    rec.fileName,
    rec.source,
    rec.provider,
    rec.kind,
    rec.category,
    rec.notes,
    rec.summary,
    rec.description,
    rec.excerpt,
    rec.excerptPreview,
    rec.sourceQuote,
    rec.rationale,
  ]) {
    if (typeof v === 'string' && v.trim().length > 0) textFragments.push(v.trim());
  }
  if (Array.isArray(rec.evidenceQuotes)) {
    for (const q of rec.evidenceQuotes) {
      if (typeof q === 'string' && q.trim().length > 0) textFragments.push(q.trim());
    }
  }
  const combinedText = textFragments.join(' ');
  if (
    AUTOMATED_ELEVATION_PROXY_SOURCE_RE.test(combinedText) ||
    NEGATED_OR_MISSING_TOPOGRAPHY_RE.test(combinedText)
  ) {
    return false;
  }

  return true;
}

function isResolvedIndependentTopographySurveySource(
  sourceId: string,
  report: SiteRagReport,
  expectedSlopePct: number | null,
): boolean {
  const trimmed = sourceId.trim();
  if (!trimmed) return false;
  if (AUTOMATED_ELEVATION_PROXY_SOURCE_RE.test(trimmed)) return false;

  const stripPrefix = (val: string): string =>
    val.replace(/^(?:doc|src|ref|citation|asset):/i, '').trim();
  const bareId = stripPrefix(trimmed);
  const refMatch = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(trimmed);
  const expectedCitationRefId = refMatch ? `citation-ref-${refMatch[1]}` : null;
  const expectedRefColonId = refMatch ? `ref:${refMatch[1]}` : null;

  const matchedSet = new Set<Record<string, unknown>>();
  const docKeys = new Set<string>();
  const refOrdinals = new Set<number>();
  let changed = false;

  const addDocKey = (raw: unknown): void => {
    if (typeof raw !== 'string') return;
    const t = raw.trim();
    if (!t) return;
    const rm = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(t);
    if (rm) {
      const ord = Number(rm[1]);
      if (!refOrdinals.has(ord)) {
        refOrdinals.add(ord);
        changed = true;
      }
      return;
    }
    if (!docKeys.has(t)) {
      docKeys.add(t);
      changed = true;
    }
    const b = stripPrefix(t);
    if (b && !docKeys.has(b)) {
      docKeys.add(b);
      changed = true;
    }
  };
  const registerMatchedRecord = (rec: Record<string, unknown>, explicitRefOrd?: number): void => {
    if (!matchedSet.has(rec)) {
      matchedSet.add(rec);
      changed = true;
    }
    if (explicitRefOrd !== undefined && !refOrdinals.has(explicitRefOrd)) {
      refOrdinals.add(explicitRefOrd);
      changed = true;
    }
    addDocKey(rec.id);
    addDocKey(rec.sourceId);
    addDocKey(rec.documentId);
    addDocKey(rec.docId);
    addDocKey(rec.citationId);
    addDocKey(rec.refId);
  };

  if (refMatch) {
    refOrdinals.add(Number(refMatch[1]));
  } else {
    addDocKey(trimmed);
    addDocKey(bareId);
  }

  let refAtOrdinalTitle = '';
  if (refMatch && Array.isArray(report.references)) {
    const refIdx = Number(refMatch[1]) - 1;
    const ref = report.references[refIdx] as unknown as Record<string, unknown> | undefined;
    if (ref && typeof ref === 'object') {
      registerMatchedRecord(ref, Number(refMatch[1]));
      refAtOrdinalTitle =
        (typeof ref.source === 'string' && ref.source.trim()) ||
        (typeof ref.title === 'string' && ref.title.trim()) ||
        (typeof ref.label === 'string' && ref.label.trim()) ||
        '';
    }
  }

  const semanticSources = (
    report.decisionAnalysis as {
      semantic?: { sources?: Record<string, Record<string, unknown>> | Array<Record<string, unknown>> };
    } | undefined
  )?.semantic?.sources;
  const semanticList: Array<Record<string, unknown>> = Array.isArray(semanticSources)
    ? semanticSources
    : semanticSources && typeof semanticSources === 'object'
      ? Object.entries(semanticSources).map(([k, v]) =>
          v && typeof v === 'object' && !Array.isArray(v) ? { id: k, ...(v as Record<string, unknown>) } : {},
        )
      : [];

  const recordMatchesDocKeys = (rec: Record<string, unknown>): boolean => {
    for (const candidate of [rec.id, rec.sourceId, rec.documentId, rec.docId, rec.citationId, rec.refId]) {
      if (typeof candidate !== 'string') continue;
      const t = candidate.trim();
      if (!t || /^(?:citation[-_:]?)?ref[-_:]?\d+$/i.exec(t)) continue;
      const b = stripPrefix(t);
      if (docKeys.has(t) || (b.length > 0 && docKeys.has(b))) return true;
    }
    return false;
  };

  // Fixed-point convergence over the finite candidate records until the entire connected component is closed
  changed = true;
  while (changed) {
    changed = false;
    if (Array.isArray(report.references)) {
      const refArray = report.references as unknown as Array<Record<string, unknown>>;
      for (let idx = 0; idx < refArray.length; idx += 1) {
        const ref = refArray[idx];
        if (!ref || typeof ref !== 'object') continue;
        const refHasTopo = ref.topographicSupport !== undefined || ref.measurements !== undefined;
        if (recordMatchesDocKeys(ref) || (refOrdinals.has(idx + 1) && refHasTopo)) {
          registerMatchedRecord(ref, refHasTopo ? idx + 1 : undefined);
        }
      }
    }

    for (const s of semanticList) {
      if (!s || typeof s !== 'object') continue;
      if (recordMatchesDocKeys(s)) {
        registerMatchedRecord(s);
        continue;
      }
      const sId =
        (typeof s.id === 'string' && s.id.trim()) ||
        (typeof s.sourceId === 'string' && s.sourceId.trim()) ||
        '';
      const sRefMatch = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(sId);
      if (
        (sRefMatch && refOrdinals.has(Number(sRefMatch[1]))) ||
        (refMatch && (sId === trimmed || sId === expectedCitationRefId || sId === expectedRefColonId))
      ) {
        const sTitle =
          (typeof s.title === 'string' && s.title.trim()) ||
          (typeof s.source === 'string' && s.source.trim()) ||
          (typeof s.label === 'string' && s.label.trim()) ||
          '';
        const sHasTopo = s.topographicSupport !== undefined || s.measurements !== undefined;
        if (sHasTopo || !refAtOrdinalTitle || (sTitle.length > 0 && sTitle === refAtOrdinalTitle)) {
          registerMatchedRecord(s);
        }
      }
    }

    const docLists = [
      (report as unknown as { documents?: unknown }).documents,
      (report as unknown as { documentRefs?: unknown }).documentRefs,
      (report as unknown as { assets?: unknown }).assets,
    ];
    for (const list of docLists) {
      if (!Array.isArray(list)) continue;
      for (const d of list as Array<Record<string, unknown>>) {
        if (!d || typeof d !== 'object') continue;
        const dId = typeof d.id === 'string' ? d.id.trim().replace(/^asset-/, '') : '';
        if (recordMatchesDocKeys(d) || (dId.length > 0 && docKeys.has(dId))) {
          registerMatchedRecord(d);
        }
      }
    }

    const rawCitations = (report as unknown as { citations?: unknown }).citations;
    if (Array.isArray(rawCitations)) {
      for (const c of rawCitations as Array<Record<string, unknown>>) {
        if (!c || typeof c !== 'object') continue;
        const cId = typeof c.id === 'string' ? c.id.trim() : '';
        const cRefMatch = /^(?:citation[-_:]?)?ref[-_:]?(\d+)$/i.exec(cId);
        if (
          recordMatchesDocKeys(c) ||
          cId === trimmed ||
          (expectedCitationRefId !== null && cId === expectedCitationRefId) ||
          (cRefMatch !== null && refOrdinals.has(Number(cRefMatch[1])))
        ) {
          registerMatchedRecord(c);
        }
      }
    }
  }

  const matchedRecords = [...matchedSet];
  // Fail closed when the sourceId/docId does not resolve to any record, or if ANY matching
  // record for that ID is explicitly unavailable/negated, or if none carries structured slope support.
  if (matchedRecords.length === 0) return false;

  return matchedRecords.every((rec) =>
    isRecordStructuredTopographySurveySupport(rec, report.siteId, expectedSlopePct),
  );
}

export interface TopographySlopeProvenanceEvaluation {
  hasFiniteZeroLandSlope: boolean;
  hasFinitePositiveLandSlope: boolean;
  hasConflictingNonZeroTopographyMetric: boolean;
  hasBoundIndependentSurveyProvenance: boolean;
  singlePointZeroSlope: boolean;
  unverifiedZeroSlopeNoProvenance: boolean;
  unverified3DepSlopeProxy: boolean;
}

/**
 * Evaluates terrain slope provenance with strict finite-number typing and explicit
 * provenance precedence:
 * 1. `land.slope` must be a finite `number` (`typeof land.slope === 'number' && Number.isFinite(land.slope)`).
 *    `null`, `undefined`, or non-numeric values never coerce to `0`.
 * 2. The target `topography` metric / `metric:site:topography` semantic evidence must not
 *    carry a conflicting non-zero value (e.g. `2` or `'2%'`) or an unrelated missing/unknown state.
 * 3. Provenance precedence: when `metric:site:topography.sourceIds` or `topography.contributingDocIds`
 *    cites a bound independent survey or document source (not merely an automated elevation-API or
 *    DEM relief proxy matching `AUTOMATED_ELEVATION_PROXY_SOURCE_RE`), that bound survey provenance
 *    takes precedence over site-level `google-elevation` / `refdata-3dep` cascade metadata and
 *    missing sample counts. Proxy-only `sourceIds` do not establish survey provenance.
 */
export function evaluateTopographySlopeProvenance(
  report: SiteRagReport,
): TopographySlopeProvenanceEvaluation {
  const land = report.datasets?.land as
    | (SiteRagReport['datasets'] extends { land?: infer L } ? L : Record<string, unknown>)
    | undefined;
  const rawLandSlope = land != null ? (land as Record<string, unknown>).slope : undefined;
  const hasFiniteLandSlope = typeof rawLandSlope === 'number' && Number.isFinite(rawLandSlope);
  const hasFiniteZeroLandSlope = hasFiniteLandSlope && Math.abs(rawLandSlope) < 1e-6;
  const hasFinitePositiveLandSlope = hasFiniteLandSlope && rawLandSlope >= 1e-6;

  const topoMetric = getSiteTopographyMetricRecord(report);
  const semanticTopoEvidence = (
    report.decisionAnalysis as {
      semantic?: { evidence?: Record<string, { state?: string; value?: unknown; sourceIds?: unknown[] }> };
    } | undefined
  )?.semantic?.evidence?.['metric:site:topography'];

  const metricNum = topoMetric && 'value' in topoMetric ? parseFiniteSlopeNumber(topoMetric.value) : null;
  const evidenceNum =
    semanticTopoEvidence && 'value' in semanticTopoEvidence
      ? parseFiniteSlopeNumber(semanticTopoEvidence.value)
      : null;

  const isAlreadyReconciledUnverifiedSlope =
    topoMetric != null &&
    RECONCILED_UNVERIFIED_SLOPE_EXPLANATION_RE.test(String(topoMetric.explanation ?? ''));

  const hasNonZeroMetricValue =
    (metricNum != null && Math.abs(metricNum) >= 1e-6) ||
    (topoMetric != null &&
      topoMetric.value != null &&
      metricNum == null &&
      !isAlreadyReconciledUnverifiedSlope);

  const hasNonZeroEvidenceValue =
    (evidenceNum != null && Math.abs(evidenceNum) >= 1e-6) ||
    (semanticTopoEvidence != null &&
      semanticTopoEvidence.state != null &&
      semanticTopoEvidence.state !== 'known');

  const hasUnrelatedMissingMetric =
    topoMetric != null &&
    Boolean(topoMetric.missing) &&
    !isAlreadyReconciledUnverifiedSlope;

  const hasConflictingNonZeroTopographyMetric =
    hasNonZeroMetricValue || hasNonZeroEvidenceValue || hasUnrelatedMissingMetric;

  const rawSourceIds = Array.isArray(semanticTopoEvidence?.sourceIds)
    ? semanticTopoEvidence.sourceIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    : [];
  const rawContributingDocIds = Array.isArray(topoMetric?.contributingDocIds)
    ? (topoMetric.contributingDocIds as unknown[]).filter(
        (id): id is string => typeof id === 'string' && id.trim().length > 0,
      )
    : [];

  const expectedSlopePct = hasFiniteLandSlope ? (rawLandSlope as number) : evidenceNum ?? metricNum;
  const hasBoundIndependentSurveyProvenance =
    rawContributingDocIds.some((docId) =>
      isResolvedIndependentTopographySurveySource(docId, report, expectedSlopePct),
    ) ||
    rawSourceIds.some((srcId) =>
      isResolvedIndependentTopographySurveySource(srcId, report, expectedSlopePct),
    );

  const pointsSampledRaw = land != null ? (land as Record<string, unknown>).elevationPointsSampled : undefined;
  const hasValidSinglePointCount =
    typeof pointsSampledRaw === 'number' &&
    Number.isFinite(pointsSampledRaw) &&
    pointsSampledRaw <= 1;

  const elevRange = land?.elevationRange as { min?: unknown; max?: unknown } | undefined;
  const minElev = elevRange != null && typeof elevRange.min === 'number' && Number.isFinite(elevRange.min) ? elevRange.min : null;
  const maxElev = elevRange != null && typeof elevRange.max === 'number' && Number.isFinite(elevRange.max) ? elevRange.max : null;

  const singlePointZeroSlope =
    hasFiniteZeroLandSlope &&
    !hasConflictingNonZeroTopographyMetric &&
    !hasBoundIndependentSurveyProvenance &&
    (hasValidSinglePointCount || (minElev != null && maxElev != null && maxElev === minElev));

  const cascadeSource = (report.siteElevationRange as { cascadeSource?: string } | undefined)?.cascadeSource;

  const unverifiedZeroSlopeNoProvenance =
    hasFiniteZeroLandSlope &&
    !hasConflictingNonZeroTopographyMetric &&
    !hasBoundIndependentSurveyProvenance &&
    !singlePointZeroSlope &&
    ((semanticTopoEvidence != null && Array.isArray(semanticTopoEvidence.sourceIds)) ||
      (typeof cascadeSource === 'string' && /^(?:google-elevation|refdata-3dep)/i.test(cascadeSource)) ||
      (pointsSampledRaw == null && minElev != null && maxElev != null && maxElev > minElev) ||
      /\b0(?:\.0+)?%\s*elevation-relief\s+proxy\b/i.test(String(report.sitingNotes ?? '')));

  const unverified3DepSlopeProxy =
    hasFinitePositiveLandSlope &&
    !hasBoundIndependentSurveyProvenance &&
    typeof cascadeSource === 'string' &&
    /^refdata-3dep/i.test(cascadeSource);

  return {
    hasFiniteZeroLandSlope,
    hasFinitePositiveLandSlope,
    hasConflictingNonZeroTopographyMetric,
    hasBoundIndependentSurveyProvenance,
    singlePointZeroSlope,
    unverifiedZeroSlopeNoProvenance,
    unverified3DepSlopeProxy,
  };
}

export function reconcileRenderTimeProvenanceAndVerdictPhrasing(report: SiteRagReport): SiteRagReport {
  let next = reconcileVerdictPhrasing(report);
  const {
    singlePointZeroSlope,
    unverifiedZeroSlopeNoProvenance,
    unverified3DepSlopeProxy,
  } = evaluateTopographySlopeProvenance(next);
  if (singlePointZeroSlope || unverifiedZeroSlopeNoProvenance) {
    const metricExplanation = singlePointZeroSlope
      ? 'Terrain slope unverified (single-point elevation sample)'
      : 'Terrain slope unverified (0% elevation-relief proxy without bound survey provenance)';
    const catReplacement = singlePointZeroSlope
      ? 'Topography / Slope: slope unverified (single-point elevation sample – excluded from scoring)'
      : 'Topography / Slope: slope unverified (0% elevation-relief proxy without bound survey provenance – excluded from scoring)';
    const nextCategories = Array.isArray(next.categories)
      ? next.categories.map((cat) => {
          if (cat.id !== 'site') return cat;
          let metricChanged = false;
          const metrics = cat.metrics?.map((m) => {
            if (m.id !== 'topography' || m.missing) return m;
            const mNum = parseFiniteSlopeNumber(m.value);
            if (mNum == null || Math.abs(mNum) >= 1e-6) return m;
            metricChanged = true;
            return {
              ...m,
              missing: true,
              status: 'amber' as const,
              score: 50,
              value: 'Unverified',
              explanation: metricExplanation,
            };
          });
          const nextCatExplanation =
            typeof cat.explanation === 'string'
            && /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/i.test(cat.explanation)
              ? cat.explanation.replace(
                  /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/gi,
                  catReplacement,
                )
              : cat.explanation;
          if (!metricChanged && nextCatExplanation === cat.explanation) return cat;
          return {
            ...cat,
            ...(metrics ? { metrics } : {}),
            ...(nextCatExplanation !== undefined ? { explanation: nextCatExplanation } : {}),
          };
        })
      : next.categories;
    const nextStrengths = next.conclusion?.topStrengths?.filter(
      (s) => !/^Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%/i.test(s),
    );
    if (nextCategories !== next.categories || (nextStrengths && nextStrengths.length !== (next.conclusion?.topStrengths?.length ?? 0))) {
      next = {
        ...next,
        ...(nextCategories ? { categories: nextCategories } : {}),
        ...(next.conclusion && nextStrengths ? { conclusion: { ...next.conclusion, topStrengths: nextStrengths } } : {}),
      };
    }
  }
  if (unverified3DepSlopeProxy && Array.isArray(next.categories)) {
    let siteCatChanged = false;
    const nextCategories = next.categories.map((cat) => {
      if (cat.id !== 'site') return cat;
      let metricChanged = false;
      const metrics = cat.metrics?.map((m) => {
        if (m.id !== 'topography' || m.status !== 'green') return m;
        const mNum = parseFiniteSlopeNumber(m.value);
        if (mNum == null || mNum <= 0) return m;
        metricChanged = true;
        return {
          ...m,
          status: 'amber' as const,
          explanation: `Regional relief proxy: ${m.value}% (local slope derivation unverified; site topographic survey required; not validated favorable readiness)`,
        };
      });
      const nextCatExplanation =
        typeof cat.explanation === 'string'
        && /Topography\s*\/\s*Slope:\s*\d+(?:\.\d+)?\s*%\s*slope\s*\(green\)/i.test(cat.explanation)
          ? cat.explanation.replace(
              /Topography\s*\/\s*Slope:\s*(\d+(?:\.\d+)?)\s*%\s*slope\s*\(green\)/gi,
              'Topography / Slope: $1% regional relief proxy (amber — local slope derivation unverified; site topographic survey required)',
            )
          : cat.explanation;
      if (!metricChanged && nextCatExplanation === cat.explanation) return cat;
      siteCatChanged = true;
      return {
        ...cat,
        ...(metrics ? { metrics } : {}),
        ...(nextCatExplanation !== undefined ? { explanation: nextCatExplanation } : {}),
      };
    });
    const nextStrengths = next.conclusion?.topStrengths?.filter(
      (s) => !/^Topography\s*\/\s*Slope:/i.test(s),
    );
    if (siteCatChanged || (nextStrengths && nextStrengths.length !== (next.conclusion?.topStrengths?.length ?? 0))) {
      next = {
        ...next,
        ...(siteCatChanged ? { categories: nextCategories } : {}),
        ...(next.conclusion && nextStrengths ? { conclusion: { ...next.conclusion, topStrengths: nextStrengths } } : {}),
      };
    }
  }
  const flood = (next.datasets as {
    flood?: {
      floodZone?: string;
      screeningCoverage?: { status?: string };
      areaAnalysis?: { zoneAreas?: Array<{ zone?: string; fraction?: number }> };
    };
  } | undefined)?.flood;
  const floodCoverage = flood?.screeningCoverage ?? next.datasets?.land?.floodCoverage;
  const zoneX = flood?.areaAnalysis?.zoneAreas?.find((z) => z.zone === 'X');
  const partialZoneXFullParcel =
    flood?.floodZone !== 'UNMAPPED'
    && floodCoverage?.status === 'partial'
    && zoneX != null
    && Number(zoneX.fraction) === 1;
  if (partialZoneXFullParcel && next.categories) {
    let categoriesChanged = false;
    const nextCategories = next.categories.map((cat) => {
      if (cat.id !== 'site') return cat;
      let metricChanged = false;
      const reconciledFloodExplanation =
        'Mapped parcel polygon is 100.0% Zone X (minimal flood hazard), held unassessed because FEMA NFHL context binding is partial/incomplete; remaining land is not confirmed flood-free.';
      const metrics = cat.metrics?.map((m) => {
        if (m.id !== 'flood_risk' || !m.missing || m.explanation === reconciledFloodExplanation) return m;
        metricChanged = true;
        return {
          ...m,
          explanation: reconciledFloodExplanation,
        };
      });
      const nextCatExplanation =
        typeof cat.explanation === 'string'
        && /Flood Risk:\s*not specified\s*\(evidence gap\s*[–—-]\s*excluded from scoring\)/i.test(cat.explanation)
          ? cat.explanation.replace(
              /Flood Risk:\s*not specified\s*\(evidence gap\s*[–—-]\s*excluded from scoring\)/gi,
              'Flood Risk: unassessed – 100.0% mapped Zone X polygon intersection held pending complete FEMA context binding (excluded from scoring)',
            )
          : cat.explanation;
      if (!metricChanged && nextCatExplanation === cat.explanation) return cat;
      categoriesChanged = true;
      return {
        ...cat,
        ...(metrics ? { metrics } : {}),
        ...(nextCatExplanation !== undefined ? { explanation: nextCatExplanation } : {}),
      };
    });
    if (categoriesChanged) {
      next = {
        ...next,
        categories: nextCategories,
      };
    }
  }
  const floodUnmapped =
    flood?.floodZone === 'UNMAPPED'
    || next.categories?.some(
      (cat) => cat.id === 'site' && cat.metrics?.some((m) => m.id === 'flood_risk' && !m.missing && /\bUNMAPPED\b/i.test(`${m.value ?? ''} ${m.explanation ?? ''}`)),
    );
  if (floodUnmapped && next.categories) {
    let categoriesChanged = false;
    const nextCategories = next.categories.map((cat) => {
      if (cat.id !== 'site') return cat;
      let metricChanged = false;
      const metrics = cat.metrics?.map((m) => {
        if (m.id !== 'flood_risk' || m.missing) return m;
        metricChanged = true;
        return {
          ...m,
          missing: true,
          status: 'amber' as const,
          score: 50,
          value: 'Unmapped',
          explanation: 'FEMA flood hazard unmapped (unverified – not confirmed low flood risk)',
        };
      });
      const nextCatExplanation =
        typeof cat.explanation === 'string'
        && /Flood\s+Risk:\s*UNMAPPED\s*\((?:green|amber|red)\)/i.test(cat.explanation)
          ? cat.explanation.replace(
              /Flood\s+Risk:\s*UNMAPPED\s*\((?:green|amber|red)\)/gi,
              'Flood Risk: unmapped (FEMA flood hazard unmapped – excluded from scoring)',
            )
          : cat.explanation;
      if (!metricChanged && nextCatExplanation === cat.explanation) return cat;
      categoriesChanged = true;
      return {
        ...cat,
        ...(metrics ? { metrics } : {}),
        ...(nextCatExplanation !== undefined ? { explanation: nextCatExplanation } : {}),
      };
    });
    if (categoriesChanged) {
      next = {
        ...next,
        categories: nextCategories,
      };
    }
  }
  if (next.categories) {
    let logisticsChanged = false;
    const nextCategories = next.categories.map((cat) => {
      if (cat.id !== 'logistics') return cat;
      let metricChanged = false;
      const metrics = cat.metrics?.map((m) => {
        if (
          m.id !== 'port_prox'
          || m.missing
          || !/\b\d+(?:\.\d+)?\s*ms\b|\blatency\b|\bIX\b/i.test(`${m.value ?? ''} ${m.explanation ?? ''}`)
        ) {
          return m;
        }
        metricChanged = true;
        return {
          ...m,
          missing: true,
          status: 'amber' as const,
          score: 50,
          value: 'Unverified',
          explanation: 'Port/airport access unverified (IX geographic-proximity network latency heuristic, not physical port or airport distance)',
        };
      });
      const nextCatExplanation =
        typeof cat.explanation === 'string'
        && /Port\s*\/\s*Airport\s+Access:\s*\d+(?:\.\d+)?\s*ms\s*\((?:green|amber|red)\)/i.test(cat.explanation)
          ? cat.explanation.replace(
              /Port\s*\/\s*Airport\s+Access:\s*\d+(?:\.\d+)?\s*ms\s*\((?:green|amber|red)\)/gi,
              'Port / Airport Access: unverified (IX network latency heuristic – excluded from scoring)',
            )
          : cat.explanation;
      if (!metricChanged && nextCatExplanation === cat.explanation) return cat;
      logisticsChanged = true;
      return {
        ...cat,
        ...(metrics ? { metrics } : {}),
        ...(nextCatExplanation !== undefined ? { explanation: nextCatExplanation } : {}),
      };
    });
    if (logisticsChanged) {
      next = {
        ...next,
        categories: nextCategories,
      };
    }
  }
  if (next.conclusion) {
    const missingLabels = new Set(
      (next.categories ?? [])
        .flatMap((cat) => cat.metrics ?? [])
        .filter((m) => m.missing && typeof m.label === 'string' && m.label.trim().length > 0)
        .map((m) => m.label.trim().toLowerCase()),
    );
    const isDisqualifiedExtreme = (entry: string): boolean => {
      const prefix = entry.split(':')[0]?.trim().toLowerCase();
      if (prefix && missingLabels.has(prefix)) return true;
      if (/\b(?:UNMAPPED|Unverified|not specified|no data|content failed quality gate|regional relief proxy|single-point elevation sample|IX geographic-proximity network latency heuristic)\b/i.test(entry)) {
        return true;
      }
      if (/^Port\s*\/\s*Airport\s+Access:.*\b\d+(?:\.\d+)?\s*ms\b/i.test(entry)) {
        return true;
      }
      return false;
    };
    const nextStrengths = next.conclusion.topStrengths?.filter((s) => !isDisqualifiedExtreme(s));
    const nextRisks = next.conclusion.topRisks?.filter((r) => !isDisqualifiedExtreme(r));
    if (
      (nextStrengths && nextStrengths.length !== (next.conclusion.topStrengths?.length ?? 0))
      || (nextRisks && nextRisks.length !== (next.conclusion.topRisks?.length ?? 0))
    ) {
      next = {
        ...next,
        conclusion: {
          ...next.conclusion,
          ...(nextStrengths ? { topStrengths: nextStrengths } : {}),
          ...(nextRisks ? { topRisks: nextRisks } : {}),
        },
      };
    }
  }
  return next;
}

/** Apply all render-time filters before HTML/PDF/DOCX display. */
export function prepareReportForRender(report: SiteRagReport): SiteRagReport {
  // Materialize withheld narrative + deep-scrub internal failure sentinels
  // first (every string leaf, not just top-level fields), then drop
  // unavailable sections. The withheld notice is real prose, so a section
  // carrying it stays visible as an open item.
  // `filterUnavailableSections` also scrubs internally (defence in depth for
  // callers that invoke it directly, e.g. SiteRagReportView), so this is
  // idempotent.
  //
  // RENDER-BOUNDARY EM-DASH SANITIZER — finally collapse em-dash (U+2014) →
  // en-dash (U+2013) across EVERY string of the resolved report so the on-screen
  // view AND the browser-print / PagedJS PDF match the house style the DOCX/PPTX
  // export serializers already enforce (`enDashOnly`). This is the ONE shared
  // boundary both report surfaces flow through: `SiteRagReportRenderer`'s render
  // memo and `PrintReportView` both call `prepareReportForRender`. Run LAST so
  // the returned object is guaranteed em-dash-free regardless of what the scrub /
  // filter steps emit; `normalizeReportDashes` is reference-preserving, so a
  // report with no em-dash stays byte-stable (zero downstream memo churn). The
  // EN-DASH (numeric ranges) is preserved. The deck running-header / footer chrome
  // — built outside the report body from the site name — reuses `normalizeDashes`.
  const withTableReferences = withDerivedTableReferences(reconcileRenderTimeProvenanceAndVerdictPhrasing(report));
  return normalizeReportDashes(filterUnavailableSections(prepareReportTextForProjection(withTableReferences)));
}
